import * as Schema from 'effect/Schema';
import React, {PropsWithChildren, useEffect, useState} from 'react';
import {deActivateBiometry, get_touch_id_availability, gonative_delete_secret} from '~/entities/user';
import {useLocationState} from '~/library/navigation';
import {RootLayout} from '~/library/ui-kit';
import {SessionExpired} from '~/processes/onboarding/ui/signup/SessionExpired';
import {appLocalStorage, appSessionStorage, clearManagedStorage} from '~/shared/persistence';
import {isAndroidOriOS, isMobileApp} from '~/shared/utils/mobileDeviceDetect';
import {BiometryBrowserScreen} from '../shared/BiometryBrowserScreen';
import {BiometrySetupScreen} from '../shared/BiometrySetupScreen';
import {PasswordIsSet} from '../shared/PasswordIsSet';
import {PhoneVerificationScreen} from './PhoneVerificationScreen';
import {SetPassword} from './SetPassword';

export function SignupPage() {
    const [step, setStep] = useLocationState('signup-step', SignupStep, Steps.phoneVerificationStart);

    const [username, setUsername] = useState<string | null>(null);
    const [password, setPassword] = useState<string | null>(null);
    const [uuid, setUuid] = useState<string | null>(null);

    useEffect(() => {
        clearManagedStorage(appSessionStorage);

        get_touch_id_availability();

        if (appLocalStorage.runBiometryActivation.get() === 'start') {
            appLocalStorage.runBiometryActivation.remove();
            deActivateBiometry();
            gonative_delete_secret();
        }
    }, []);

    if (step === Steps.setPassword && uuid) {
        return (
            <SignupLayout>
                <SetPassword
                    uuid={uuid}
                    onPasswordSet={password => {
                        setPassword(password);
                        setStep(Steps.passwordIsSet, {replace: true});
                    }}
                    onSessionExpired={() => setStep(Steps.sessionsExpired, {replace: true})}
                />
            </SignupLayout>
        );
    }

    if (step === Steps.sessionsExpired) {
        const handleStartAgain = () => {
            setUuid(null);
            setPassword(null);
            setStep(Steps.phoneVerificationStart, {replace: true});
        };
        return <SessionExpired onStartAgain={handleStartAgain} />;
    }

    if (step === Steps.passwordIsSet) {
        const onPasswordIsSet = () => {
            const hasTouchId = appLocalStorage.hasTouchId.get();

            if (hasTouchId) {
                setStep(Steps.biometrySetup, {replace: true});
            } else if (isAndroidOriOS() && !isMobileApp()) {
                setStep(Steps.biometryBrowser, {replace: true});
            } else {
                window.restartAppAt('/');
            }
        };

        return <PasswordIsSet onButtonClick={onPasswordIsSet} onUnmount={onPasswordIsSet} />;
    }

    if (step === Steps.biometrySetup && username && password) {
        function restartApp() {
            window.restartAppAt('/');
        }

        return (
            <SignupLayout disableBack>
                <BiometrySetupScreen
                    password={password}
                    onSuccess={restartApp}
                    onLater={restartApp}
                    callPoint={'signupForm'}
                />
            </SignupLayout>
        );
    }

    if (step === Steps.biometryBrowser) {
        return (
            <SignupLayout disableBack>
                <BiometryBrowserScreen />
            </SignupLayout>
        );
    }

    return (
        <SignupLayout>
            <PhoneVerificationScreen
                onVerificationSuccess={({username, uuid}) => {
                    setUsername(username);
                    setUuid(uuid);
                    setStep(Steps.setPassword, {replace: true});
                }}
                isOTP={step === Steps.phoneVerificationOTP}
                goToOTP={() => setStep(Steps.phoneVerificationOTP)}
            />
        </SignupLayout>
    );
}

function SignupLayout({children, disableBack}: PropsWithChildren<{disableBack?: boolean}>) {
    return (
        <RootLayout title={'Sign up'} navigateBack={disableBack ? false : undefined}>
            {children}
        </RootLayout>
    );
}

const Steps = {
    phoneVerificationStart: 'phone-verification-start',
    phoneVerificationOTP: 'phone-verification-otp',
    setPassword: 'set-password',
    passwordIsSet: 'password-is-set',
    sessionsExpired: 'sessions-expired',
    biometrySetup: 'biometry-setup',
    biometryBrowser: 'biometry-browser',
} as const;
const SignupStep = Schema.Literal(...Object.values(Steps));
